import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Copy, MapPin } from "lucide-react";

export interface OrderDeliveryAddress {
  contact_name?: string | null;
  company_name?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone_number?: string | null;
}

interface DeliveryAddressBlockProps {
  address?: OrderDeliveryAddress | null;
  /** Legacy free-text address stored directly on the order */
  legacyAddress?: string | null;
  title?: string;
  compact?: boolean;
}

export const formatDeliveryAddress = (address: OrderDeliveryAddress): string =>
  [
    address.contact_name,
    address.company_name,
    address.address_line_1,
    address.address_line_2,
    address.city,
    address.postal_code,
    address.country,
  ]
    .filter((line) => !!line && String(line).trim().length > 0)
    .join("\n");

export const DeliveryAddressBlock = ({
  address,
  legacyAddress,
  title = "Delivery Address",
  compact = false,
}: DeliveryAddressBlockProps) => {
  const { toast } = useToast();

  const formatted = address ? formatDeliveryAddress(address) : "";
  const text = formatted || (legacyAddress ?? "");

  if (!text) return null;

  const handleCopy = async () => {
    const fullText = address?.phone_number ? `${text}\n${address.phone_number}` : text;
    try {
      await navigator.clipboard.writeText(fullText);
      toast({ title: "Address copied", description: "The delivery address is on your clipboard." });
    } catch {
      toast({
        title: "Could not copy",
        description: "Please select the address and copy it manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className={compact ? "rounded-md border bg-muted/30 p-3" : ""}>
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          {title}
        </h4>
        <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 px-2">
          <Copy className="h-3.5 w-3.5 mr-1" />
          Copy
        </Button>
      </div>
      <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{text}</div>
      {address?.phone_number && (
        <div className="text-sm mt-2">
          <span className="font-medium">Phone:</span>{" "}
          <span className="text-muted-foreground">{address.phone_number}</span>
        </div>
      )}
    </div>
  );
};
